import { createContext, useContext, type CSSProperties, type ReactNode } from 'react'
import { TrendingDown, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CellMark, type CellMarkState } from './CommentableCell'
import { formatRevenueCompact } from './formatters'

// What a revision changes against the published plan, keyed by the draft's
// ids (TopDownPlans::RevisionDiff). The review page renders the draft and
// looks up the published "was" value per cell.
export interface RevisionBaseline {
  node_targets: Record<string, number>
  added_node_ids: string[]
  removed_nodes: { id: string; name: string; depth: number; target_revenue: number }[]
  channel_targets: Record<string, Record<string, number>>
  channel_totals: Record<string, number>
  quarter_targets: Record<string, Record<string, number>>
  total_target: number
  // Quarterly revision only (TopDownPlans::QuarterlyRevisionDiff): the
  // full-year "now" — sold months + remaining target — that the published
  // number is compared with. Absent on a full-plan diff.
  node_full_year?: Record<string, number>
  channel_full_year?: Record<string, Record<string, number>>
  channel_totals_full_year?: Record<string, number>
  total_full_year?: number
  // The two versions the full-year lines compare: "Full year v3" (now) over
  // "Full year v2" (was).
  version_numbers?: { now: number; was: number }
  // No published version at all (a plan's first submit): every row is added
  // and no cell has a published value to show on a red line.
  first_publish: boolean
  summary: {
    changed_targets: number
    added_nodes: number
    removed_nodes: number
    changed_channel_cells: number
    changed_quarter_cells: number
    any_changes: boolean
  }
}

// GitHub diff colours: green for what the revision adds, red for what it
// replaces or removes. --success-surface is a much stronger tint than
// --destructive-surface, so the green is thinned to sit at the same weight.
// Tune here, never the global tokens.
const ADDED_BG = 'color-mix(in srgb, var(--success-surface) 40%, var(--background))'
const REMOVED_BG = 'color-mix(in srgb, var(--destructive-surface) 80%, var(--background))'
const SIGN_COLOR = { added: 'var(--success)', removed: 'var(--destructive)' } as const

type Kind = 'added' | 'removed'

// Whole-row background for a row the revision added or removed. Spread onto
// the <tr>, and onto any sticky cell that paints its own background.
export const addedRowStyle: CSSProperties = { background: ADDED_BG }
export const removedRowStyle: CSSProperties = { background: REMOVED_BG }

export function isChanged(was: number | undefined, now: number): boolean {
  return was !== undefined && was !== now
}

// The published value behind a total or quarter cell, or undefined when there
// is nothing to compare against: no baseline, or a first publish. A published
// cell that does not exist counts as 0.
export function publishedValue(
  baseline: RevisionBaseline | null,
  lookup: (b: RevisionBaseline) => number | undefined
): number | undefined {
  if (!baseline || baseline.first_publish) return undefined
  return lookup(baseline) ?? 0
}

// A big move in a diff cell: the value changed by more than BIG_MOVE_PCT of
// the published value AND by at least the currency's BIG_MOVE_MIN. Trial
// rule from the CEO review (2026-10-07): >20% and ≥ ₫10B. A currency with no
// minimum here shows no arrows; a plan with no currency is a legacy VND plan.
const BIG_MOVE_PCT = 0.2
const BIG_MOVE_MIN: Record<string, number> = { VND: 10_000_000_000 }

export function bigMoveMin(currency: string | null | undefined): number | null {
  return BIG_MOVE_MIN[currency ?? 'VND'] ?? null
}

export function isBigMove(was: number, now: number, min: number | null): boolean {
  if (min === null) return false
  const delta = Math.abs(now - was)
  if (delta < min) return false
  return was === 0 || delta / Math.abs(was) > BIG_MOVE_PCT
}

// The diff tables' minimum, set once by PlanBreakdownSections from the plan's
// currency, so every DiffValue can mark its own big moves.
export const BigMoveMinContext = createContext<number | null>(null)

// Stock-chart arrow: green up for a rise, red down for a drop.
export function MoveArrow({ up, className = 'h-4 w-4' }: { up: boolean; className?: string }) {
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <Icon
      className={`mr-1 inline shrink-0 align-text-bottom ${className}`}
      style={{ color: up ? 'var(--success)' : 'var(--destructive)' }}
      aria-hidden
    />
  )
}

// The arrow in front of a changed cell's new value, only on a big move.
export function BigMoveArrow({ was, now }: { was: number | undefined; now: number }) {
  const min = useContext(BigMoveMinContext)
  if (was === undefined || !isBigMove(was, now, min)) return null
  return <MoveArrow up={now > was} className="h-3.5 w-3.5" />
}

// Sign in front of a row name, like a diff gutter: + added, − removed.
export function RowSign({ kind }: { kind: Kind }) {
  return (
    <span className="mr-2 inline-block w-3 font-mono" style={{ color: SIGN_COLOR[kind] }}>
      {kind === 'added' ? '+' : '−'}
    </span>
  )
}

// One coloured line of a diff: green for the new value, red for the
// published one. Colour only, no +/− sign: next to a number (or a negative
// growth %) the sign reads as part of the value. Exported for cells that lay
// the two lines out themselves.
// `className` overrides the default sizes (the published line is smaller).
export function DiffLine({ kind, className, children }: { kind: Kind; className?: string; children: ReactNode }) {
  const added = kind === 'added'
  return (
    <div
      className={cn('text-right rounded-sm px-1.5', !added && 'text-xs', className)}
      style={{ background: added ? ADDED_BG : REMOVED_BG }}
    >
      {children}
    </div>
  )
}

// A cell value under review. Unchanged: the value as-is. Changed: the new
// value on a green line (with an arrow on a big move), the published value on
// a red line below.
// `format` is the caller's own display (full/compact, zero handling); a
// published zero always shows as —.
// `mark` (review page) adds the comment count before the new value.
export function DiffValue({ was, now, format, mark }: {
  was: number | undefined
  now: number
  format: (v: number) => string
  mark?: CellMarkState
}) {
  if (!isChanged(was, now)) return <CellMark mark={mark}>{format(now)}</CellMark>
  return (
    <>
      <DiffLine kind="added"><CellMark mark={mark}><span className="whitespace-nowrap"><BigMoveArrow was={was} now={now} />{format(now)}</span></CellMark></DiffLine>
      <DiffLine kind="removed">{was ? format(was) : '—'}</DiffLine>
    </>
  )
}

// Key for the green/red lines, shown above the diff tables. Sits on its own
// white card: the tints are mixed against --background and fade on the grey page.
// `children` adds more keys after the colours (e.g. what "Past" means).
export function DiffLegend({ currency, className, children }: { currency: string | null; className?: string; children?: ReactNode }) {
  const min = bigMoveMin(currency)
  const swatch = (bg: string) => (
    <span className="inline-block h-3 w-6 rounded-sm align-middle" style={{ background: bg }} />
  )
  return (
    <div
      className={`inline-flex items-center gap-4 rounded-md border px-3 py-2 text-sm text-foreground ${className ?? ''}`}
      style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
    >
      <span className="flex items-center gap-1.5">{swatch(ADDED_BG)} New number</span>
      <span className="flex items-center gap-1.5">{swatch(REMOVED_BG)} Old number (published plan)</span>
      {min !== null && (
        <span className="flex items-center">
          <MoveArrow up /><MoveArrow up={false} />
          Big rise/drop over {BIG_MOVE_PCT * 100}% and at least {formatRevenueCompact(min, currency)}
        </span>
      )}
      {children}
    </div>
  )
}

// The number on the review page's "Plan changes" tab: every green/red mark
// counts once (node targets, added/removed rows, channel and quarter cells).
// Plan and channel totals are not counted — the backend summary leaves them
// out. null on a first publish, where there is nothing to compare against.
export function changeCount(baseline: RevisionBaseline): number | null {
  if (baseline.first_publish) return null
  const { summary } = baseline
  return summary.changed_targets + summary.added_nodes + summary.removed_nodes +
    summary.changed_channel_cells + summary.changed_quarter_cells
}
